import React, { useEffect, useState } from 'react';
import { FiUsers, FiTrendingUp, FiCheckCircle, FiDollarSign } from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import { useCurrency } from '../../app/CurrencyContext';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import panelStyles from '../../styles/adminPanels.module.css';
import styles from './AdminRewards.module.css';
import axiosClient from '../../api/axiosClient';

interface Row {
  userId: string; fullName: string; email: string;
  directBonus: number; overrideBonus: number; totalBonus: number; claimed: number; claimable: number;
}
interface Report {
  totalDirect: number; totalOverride: number; totalRewards: number; totalClaimed: number; rows: Row[];
}

const AdminRewards: React.FC = () => {
  const { formatFromInr } = useCurrency();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosClient.get('/admin/rewards').then(({ data }) => setReport(data)).finally(() => setLoading(false));
  }, []);

  return (
    <AdminShell>
      <PageHeader title="Reward Report" subtitle="Direct and override referral bonuses earned across all users." action={<AdminWalletBadge />} />

      <div className={panelStyles.page}>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div><span>Total Direct Bonus</span><b>{formatFromInr(report?.totalDirect ?? 0)}</b></div>
            <FiDollarSign className={styles.statIcon} />
          </div>
          <div className={styles.statCard}>
            <div><span>Total Override Bonus</span><b>{formatFromInr(report?.totalOverride ?? 0)}</b></div>
            <FiTrendingUp className={styles.statIcon} />
          </div>
          <div className={styles.statCard}>
            <div><span>Total Rewards</span><b>{formatFromInr(report?.totalRewards ?? 0)}</b></div>
            <FiUsers className={styles.statIcon} />
          </div>
          <div className={styles.statCard}>
            <div><span>Total Claimed</span><b>{formatFromInr(report?.totalClaimed ?? 0)}</b></div>
            <FiCheckCircle className={styles.statIcon} />
          </div>
        </div>

        <div className={panelStyles.tableWrap}>
          {loading && <div className={panelStyles.loading}><span className={panelStyles.spinnerLg} /></div>}
          {!loading && (!report || report.rows.length === 0) && <div className={panelStyles.empty}>No referral rewards earned yet.</div>}
          {!loading && report && report.rows.length > 0 && (
            <table className={panelStyles.table}>
              <thead>
                <tr><th>User</th><th>Direct</th><th>Override</th><th>Total</th><th>Claimed</th><th>Claimable</th></tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.userId}>
                    <td>
                      <div className={panelStyles.nameCell}>{r.fullName}</div>
                      <div className={panelStyles.subCell}>{r.email}</div>
                    </td>
                    <td>₹{r.directBonus.toLocaleString()}</td>
                    <td>₹{r.overrideBonus.toLocaleString()}</td>
                    <td className={panelStyles.nameCell}>₹{r.totalBonus.toLocaleString()}</td>
                    <td style={{ color: 'var(--success)' }}>₹{r.claimed.toLocaleString()}</td>
                    <td style={{ color: 'var(--warning)' }}>₹{r.claimable.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminRewards;
